import { createRootRoute, createRoute, createRouter, Outlet } from '@tanstack/react-router';
import { AuthedLayout } from '@/routes/_authed';
import { CollectionDetailPage } from '@/routes/_authed/collection-detail';
import { DashboardPage } from '@/routes/_authed/dashboard';
import { DestinationDetailPage } from '@/routes/_authed/destination-detail';
import { DestinationRunDetailPage } from '@/routes/_authed/destination-run-detail';
import { DestinationsPage } from '@/routes/_authed/destinations';
import { AddDestinationPage } from '@/routes/_authed/destinations-add';
import { LandingRoute } from '@/routes/_authed/index';
import { OnboardingPage } from '@/routes/_authed/onboarding';
import { SettingsPage } from '@/routes/_authed/settings';
import { SourceDetailPage } from '@/routes/_authed/source-detail';
import { SourcesPage } from '@/routes/_authed/sources';
import { AddSourcePage } from '@/routes/_authed/sources-add';
import { ImportPage } from '@/routes/_authed/sources-import';
import { SignalImportPage } from '@/routes/_authed/sources-signal-import';
import { AcceptInvitationPage } from '@/routes/accept-invitation';
import { ForgotPasswordPage } from '@/routes/forgot-password';
import { LoginPage } from '@/routes/login';
import { ConsentPage } from '@/routes/oauth/consent';
import { ResetPasswordPage } from '@/routes/reset-password';
import { SignupPage } from '@/routes/signup';
import { VerifyEmailPage } from '@/routes/verify-email';

// Root route — no layout, just an outlet.
const rootRoute = createRootRoute({ component: Outlet });

// Public routes. Login/signup read in-flight OAuth grant params
// (?client_id=…&code=…&state=…, better-auth's loginPage bounce shape) via
// useSearch({ strict: false }) — no validateSearch here so every other
// navigate({ to: '/login' }) call site stays search-less.
const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  component: LoginPage,
});
const signupRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/signup',
  component: SignupPage,
});
const verifyEmailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/verify-email',
  component: VerifyEmailPage,
});
// Public password recovery (plan 2026-07-24-password-reset). /reset-password
// is where better-auth's GET callback lands the user with ?token=… or
// ?error=INVALID_TOKEN — both pages read search via useSearch({strict:false}).
const forgotPasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/forgot-password',
  component: ForgotPasswordPage,
});
const resetPasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/reset-password',
  component: ResetPasswordPage,
});
// Public (reachable logged-out): an invited person lands here from the email.
const acceptInvitationRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/accept-invitation/$invitationId',
  component: AcceptInvitationPage,
});
// Public (bounces itself to /login when logged out): better-auth 302s here
// from the authorize endpoint once the user must approve a client.
const oauthConsentRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/oauth/consent',
  validateSearch: (search: Record<string, unknown>) => ({
    consent_code: typeof search.consent_code === 'string' ? search.consent_code : undefined,
    client_id: typeof search.client_id === 'string' ? search.client_id : undefined,
    scope: typeof search.scope === 'string' ? search.scope : undefined,
  }),
  component: ConsentPage,
});

// Authenticated layout route — guards all children.
const authedRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: '_authed',
  component: AuthedLayout,
});

// Index — routes to the dashboard or the setup wizard based on whether the
// workspace has connected anything yet (see LandingRoute).
const indexRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/',
  component: LandingRoute,
});

const dashboardRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/dashboard',
  component: DashboardPage,
});

const onboardingRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/onboarding',
  component: OnboardingPage,
});

const sourcesRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/sources',
  component: SourcesPage,
});

const sourcesAddRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/sources/add',
  component: AddSourcePage,
});

// Destinations v1 (plan 2026-08-04-destinations-v1.md §5.5, D15): pure index +
// add wizard + per-destination View page — the sources pattern, mirrored.
const destinationsRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/destinations',
  component: DestinationsPage,
});

const destinationsAddRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/destinations/add',
  component: AddDestinationPage,
});

const destinationDetailRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/destinations/$destinationId',
  component: DestinationDetailPage,
});

const destinationRunDetailRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/destinations/$destinationId/runs/$runId',
  component: DestinationRunDetailPage,
});

const sourcesImportRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/sources/$collectionId/import',
  component: ImportPage,
});

// Collection-level detail view — shows a single group or chat's messages.
// Static "collection" segment wins over the dynamic $connector param in
// /sources/$connector/$accountId, so there is no shadowing ambiguity.
const collectionDetailRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/sources/collection/$collectionId',
  component: CollectionDetailPage,
});

// Account-level detail view — shows connection health, data activity, and a
// message log. Path mirrors the connector-accounts API: /sources/:connector/:accountId.
const sourceDetailRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/sources/$connector/$accountId',
  component: SourceDetailPage,
});

// Signal's importer is account-wide rather than per-collection (one backup
// carries every chat), so it gets its own path instead of a collection param.
const sourcesSignalImportRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/sources/signal/import',
  component: SignalImportPage,
});

const settingsRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/settings',
  component: SettingsPage,
});

const routeTree = rootRoute.addChildren([
  loginRoute,
  signupRoute,
  verifyEmailRoute,
  forgotPasswordRoute,
  resetPasswordRoute,
  acceptInvitationRoute,
  oauthConsentRoute,
  authedRoute.addChildren([
    indexRoute,
    dashboardRoute,
    onboardingRoute,
    sourcesRoute,
    sourcesAddRoute,
    destinationsRoute,
    destinationsAddRoute,
    destinationDetailRoute,
    destinationRunDetailRoute,
    sourcesImportRoute,
    collectionDetailRoute,
    sourceDetailRoute,
    sourcesSignalImportRoute,
    settingsRoute,
  ]),
]);

export const router = createRouter({ routeTree });

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}
